import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { NAV, BRAND } from "@/lib/content";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  return (
    <>
      <header data-testid="navbar" className="fixed top-4 inset-x-0 z-50 px-4">
        <div className="max-w-[760px] mx-auto glass rounded-full pl-4 pr-2 py-2 flex items-center justify-between shadow-[0_8px_30px_-12px_rgba(11,11,13,0.25)]">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <img src={BRAND.logo} alt="ChiCha Lounge DC logo" className="h-9 w-auto" />
            <span className="font-display font-extrabold text-[15px] tracking-tight text-neutral-900 hidden sm:block">CHI-CHA</span>
          </Link>
          <div className="flex items-center gap-2">
            <button data-testid="nav-buy-tickets" onClick={() => navigate("/buy-tickets")} className="hidden sm:inline-flex btn-gold !py-2 !px-4 text-sm">Reserve</button>
            <button data-testid="nav-menu-toggle" onClick={() => setOpen(true)} className="btn-dark !py-2 !px-5 text-sm">Menu</button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="menu-overlay"
            data-lenis-prevent
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-[#0B0B0D] text-white overflow-y-auto"
          >
            <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
              <img src={BRAND.logo} alt="ChiCha" className="h-14 w-auto brightness-150 contrast-125" style={{ filter: "brightness(1.5) contrast(1.25) drop-shadow(0 0 10px rgba(255,255,255,0.9)) drop-shadow(0 0 24px rgba(212,175,55,0.85)) drop-shadow(0 0 40px rgba(212,175,55,0.6))" }} />
              <button data-testid="menu-close" onClick={() => setOpen(false)} className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"><X size={20} /></button>
            </div>
            <nav className="max-w-[1300px] mx-auto px-6 md:px-10 py-6 md:py-8 grid grid-cols-1 lg:grid-cols-2 lg:gap-x-14">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.04, duration: 0.4 }}
                  className="border-b border-white/10"
                >
                  <Link to={n.to} data-testid={`menu-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="group flex items-center justify-between py-3.5 md:py-4">
                    <span className="flex items-baseline gap-3 flex-wrap">
                      <span className="display-mega text-white/80 text-3xl md:text-5xl group-hover:text-white transition-colors">{n.label}</span>
                      {n.desc && <span className="text-white/40 text-xs md:text-sm uppercase tracking-[0.18em] group-hover:text-white/70 transition-colors">{n.desc}</span>}
                    </span>
                    <ArrowUpRight className="text-white/30 group-hover:text-white transition-colors" size={28} />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="max-w-[1300px] mx-auto px-6 md:px-10 pb-14 flex flex-wrap gap-4 items-center justify-between">
              <div className="text-white/50 text-sm">
                <div>{BRAND.address}</div>
                <a href={`tel:${BRAND.phoneRaw}`} className="hover:text-white">{BRAND.phone}</a>
              </div>
              <button onClick={() => { setOpen(false); navigate("/buy-tickets"); }} className="btn-gold">Reserve <ArrowUpRight size={16} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
